import type { FormEvent } from 'react';
import React, { useState } from 'react';

import {
  AdornedInput,
  Box,
  FormField,
  FormHelp,
  Input,
  Label,
  Paragraph,
  TextButton,
  ToggleButton,
  ToggleButtonGroup,
} from '@zillow/constellation-10';

import { PETS } from '../../../../constants/index';
import { GENDERS, PET_TYPES } from '../../../../constants/stringConstants';
import type Field from '../../../../models/Field';
import { PetSection } from '../../../../models/index';
import { hyphenateString } from '../../../../utils/stringParsing';

type Props = {
  index: number;
  onFieldChange: (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  onRemovePet: (event: FormEvent<HTMLElement>) => void;
  breedField: Field;
  genderField: Field;
  licenseField: Field;
  nameField: Field;
  typeField: Field;
  weightField: Field;
  breedValue: string;
  genderValue: string;
  licenseValue: string;
  nameValue: string;
  typeValue: string;
  weightValue: string;
  typeHasError: boolean;
  genderHasError: boolean;
  weightHasError: boolean;
};

/*
  Note we maintain a local typeValue state to defer committing the value until a user unfocuses the "Type of pet"
  input field. This allows folks to, for example, type "caterpillar" without the type automatically switching to
  "cat" before the user finishes typing
*/

export default function PetInformation({
  typeValue,
  index,
  onRemovePet,
  onFieldChange,
  breedField,
  genderField,
  licenseField,
  nameField,
  typeField,
  weightField,
  breedValue,
  genderValue,
  licenseValue,
  nameValue,
  weightValue,
  typeHasError,
  genderHasError,
  weightHasError,
}: Props): JSX.Element {
  const [stateTypeValue, setStateTypeValue] = useState(typeValue);
  const [typeIsInitialized, setTypeIsInitialized] = useState(typeValue !== '');

  function onTypeButtonClick(value: string, fieldName: string): void {
    setTypeIsInitialized(true);
    setStateTypeValue(value);
    // Simulate a change event for the parent handler
    const syntheticEvent = {
      currentTarget: {
        value,
        dataset: { fieldName },
      },
      target: {
        value,
      },
    } as unknown as React.ChangeEvent<HTMLInputElement>;
    onFieldChange(syntheticEvent);
  }

  function onTypeChange(event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): void {
    const { value } = event.currentTarget;

    setTypeIsInitialized(true);
    setStateTypeValue(value);
  }

  function onGenderButtonClick(value: string, fieldName: string): void {
    // Simulate a change event for the parent handler
    const syntheticEvent = {
      currentTarget: {
        value,
        dataset: { fieldName },
      },
      target: {
        value,
      },
    } as unknown as React.ChangeEvent<HTMLInputElement>;
    onFieldChange(syntheticEvent);
  }

  // Note we're determining isOtherType based on Props.typeValue rather than the local typeValue.
  // The user might still be typing the local typeValue and we don't want branching logic to be triggered
  // while that value is in flux.
  const isOtherType = typeIsInitialized && PetSection.isOtherType(typeValue);

  return (
    <React.Fragment key={index}>
      {index > 0 && (
        <Box css={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Paragraph textStyle="body-bold">Pet #{index + 1}</Paragraph>
          <TextButton data-index={index} onClick={onRemovePet}>
            Delete
          </TextButton>
        </Box>
      )}
      <FormField
        css={{ marginBottom: 'layout.tight' }}
        required
        label={<Label id={hyphenateString(`${PETS.LABEL.TYPE}-${index}`)}>{PETS.LABEL.TYPE}</Label>}
        control={
          <ToggleButtonGroup conjoined aria-label="pet type" css={{ marginTop: 'layout.tighter', width: '100%' }}>
            <ToggleButton
              selected={PetSection.isDogType(typeField.enteredValue)}
              onClick={() => onTypeButtonClick(PET_TYPES.DOG, typeField.fieldName)}
            >
              Dog
            </ToggleButton>
            <ToggleButton
              selected={PetSection.isCatType(typeField.enteredValue)}
              onClick={() => onTypeButtonClick(PET_TYPES.CAT, typeField.fieldName)}
            >
              Cat
            </ToggleButton>
            <ToggleButton selected={isOtherType} onClick={() => onTypeButtonClick('', typeField.fieldName)}>
              Other
            </ToggleButton>
          </ToggleButtonGroup>
        }
      />
      {isOtherType ? (
        <FormField
          required
          css={{ marginBottom: 'layout.tight' }}
          error={typeHasError}
          label={<Label id={hyphenateString(`other-type-${index}`)}>{PETS.LABEL.OTHER_TYPE}</Label>}
          control={
            <Input
              aria-labelledby={hyphenateString(`other-type-${index}`)}
              data-field-name={typeField.fieldName}
              value={stateTypeValue}
              onChange={onTypeChange}
              onBlur={onFieldChange}
            />
          }
          description={typeHasError && <FormHelp>{typeField.getErrorMessage()}</FormHelp>}
        />
      ) : (
        <FormField
          optional
          css={{ marginBottom: 'layout.tight' }}
          label={<Label id={hyphenateString(`${PETS.LABEL.GENDER}-${index}`)}>{PETS.LABEL.GENDER}</Label>}
          control={
            <ToggleButtonGroup conjoined aria-label="pet gender" css={{ marginTop: 'layout.tighter', width: '100%' }}>
              <ToggleButton
                selected={genderValue === GENDERS.MALE}
                onClick={() => onGenderButtonClick(GENDERS.MALE, genderField.fieldName)}
              >
                Male
              </ToggleButton>
              <ToggleButton
                selected={genderValue === GENDERS.FEMALE}
                onClick={() => onGenderButtonClick(GENDERS.FEMALE, genderField.fieldName)}
              >
                Female
              </ToggleButton>
            </ToggleButtonGroup>
          }
        />
      )}
      <FormField
        optional
        css={{ marginBottom: 'layout.tight' }}
        label={<Label id={hyphenateString(`${PETS.LABEL.NAME}-${index}`)}>{PETS.LABEL.NAME}</Label>}
        control={
          <Input
            aria-labelledby={hyphenateString(`${PETS.LABEL.NAME}-${index}`)}
            data-field-name={nameField.fieldName}
            data-test="nameField"
            value={nameValue}
            onChange={onFieldChange}
          />
        }
      />
      {!isOtherType && (
        <FormField
          optional
          css={{ marginBottom: 'layout.tight' }}
          label={<Label id={hyphenateString(`${PETS.LABEL.BREED}-${index}`)}>{PETS.LABEL.BREED}</Label>}
          control={
            <Input
              aria-labelledby={hyphenateString(`${PETS.LABEL.BREED}-${index}`)}
              data-field-name={breedField.fieldName}
              value={breedValue}
              onChange={onFieldChange}
            />
          }
        />
      )}
      <FormField
        optional
        css={{ marginBottom: 'layout.tight' }}
        error={weightHasError}
        label={<Label id={hyphenateString(`${PETS.LABEL.WEIGHT}-${index}`)}>{PETS.LABEL.WEIGHT}</Label>}
        control={
          <AdornedInput.Root error={weightHasError}>
            <AdornedInput.Input
              aria-labelledby={hyphenateString(`${PETS.LABEL.WEIGHT}-${index}`)}
              data-field-name={weightField.fieldName}
              value={weightValue}
              onChange={onFieldChange}
            />
            <AdornedInput.Adornment>lbs</AdornedInput.Adornment>
          </AdornedInput.Root>
        }
        description={weightHasError && <FormHelp>{weightField.getErrorMessage()}</FormHelp>}
      />
      <FormField
        optional
        label={<Label id={hyphenateString(`${PETS.LABEL.LICENSE_NUMBER}-${index}`)}>{PETS.LABEL.LICENSE_NUMBER}</Label>}
        control={
          <Input
            aria-labelledby={hyphenateString(`${PETS.LABEL.LICENSE_NUMBER}-${index}`)}
            data-field-name={licenseField.fieldName}
            value={licenseValue}
            onChange={onFieldChange}
          />
        }
      />
    </React.Fragment>
  );
}
